import { createSlice } from '@reduxjs/toolkit';

const DASHBOARD_LAYOUT_VERSION = 2;

// Default dashboard layout
const defaultLayout = [
  { id: 'tasks', type: 'tasks', position: { row: 0, col: 0 }, size: { w: 1, h: 1 } },
  { id: 'shows', type: 'shows', position: { row: 0, col: 1 }, size: { w: 1, h: 1 } },
  { id: 'schedule', type: 'schedule', position: { row: 0, col: 2 }, size: { w: 1, h: 1 } },
  { id: 'demos', type: 'demos', position: { row: 1, col: 0 }, size: { w: 1, h: 1 } },
  { id: 'releases', type: 'releases', position: { row: 1, col: 1 }, size: { w: 1, h: 1 } },
  { id: 'notes', type: 'notes', position: { row: 1, col: 2 }, size: { w: 1, h: 1 } },
  { id: 'ticketCount', type: 'ticketCount', position: { row: 2, col: 0 }, size: { w: 1, h: 1 } },
  { id: 'team', type: 'team', position: { row: 2, col: 1 }, size: { w: 1, h: 1 } },
  { id: 'analytics', type: 'analytics', position: { row: 2, col: 2 }, size: { w: 1, h: 1 } },
  { id: 'works', type: 'works', position: { row: 3, col: 0 }, size: { w: 1, h: 1 } },
];

// Available module types
export const MODULE_TYPES = {
  tasks: { label: 'Tasks', icon: 'tasks' },
  shows: { label: 'Shows', icon: 'shows' },
  schedule: { label: 'Schedule', icon: 'schedule' },
  demos: { label: 'Demos', icon: 'demos' },
  releases: { label: 'Releases', icon: 'releases' },
  notes: { label: 'Notes', icon: 'notes' },
  ticketCount: { label: 'Ticket count', icon: 'ticket' },
  team: { label: 'Team', icon: 'team' },
  analytics: { label: 'Analytics', icon: 'analytics' },
  works: { label: 'Works', icon: 'works' },
};

// Try to load saved layout from localStorage
const loadSavedLayout = () => {
  try {
    const saved = localStorage.getItem('dizee_dashboard_layout');
    console.log('🔍 Loading saved dashboard layout:', saved);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Back-compat: older versions stored raw array
      if (Array.isArray(parsed)) {
        console.log('📦 Found old array format, merging with defaults');
        return mergeWithDefaultLayout(parsed);
      }
      // New format: { version, layout }
      if (parsed && Array.isArray(parsed.layout)) {
        console.log('📦 Found new format with version', parsed.version);
        return mergeWithDefaultLayout(parsed.layout);
      }
    }
  } catch (e) {
    console.error('Failed to load dashboard layout:', e);
  }
  console.log('📦 No saved layout, using defaultLayout');
  return defaultLayout;
};

const saveLayout = (layout) => {
  try {
    localStorage.setItem(
      'dizee_dashboard_layout',
      JSON.stringify({ version: DASHBOARD_LAYOUT_VERSION, layout })
    );
  } catch (e) {
    console.error('Failed to save dashboard layout:', e);
  }
};

// Ensure any newly introduced default modules show up for existing users
const mergeWithDefaultLayout = (layout) => {
  if (!Array.isArray(layout)) return defaultLayout;

  const existingIds = new Set(layout.map((m) => m?.id).filter(Boolean));
  const merged = [...layout];

  for (const mod of defaultLayout) {
    if (!existingIds.has(mod.id)) {
      merged.push(mod);
    }
  }

  return merged;
};

const initialLayout = (() => {
  const loaded = loadSavedLayout();
  const merged = mergeWithDefaultLayout(loaded);
  // Always re-save in the new format so future updates can be versioned
  saveLayout(merged);
  return merged;
})();

const initialState = {
  layout: initialLayout,
  editMode: false,
  newModuleDropdownOpen: false,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    ensureDefaultModules: (state) => {
      // redux-persist can rehydrate older dashboard state; ensure new default modules exist
      state.layout = mergeWithDefaultLayout(state.layout);
      saveLayout(state.layout);
    },
    setEditMode: (state, action) => {
      state.editMode = action.payload;
    },
    toggleEditMode: (state) => {
      state.editMode = !state.editMode;
    },
    addModule: (state, action) => {
      const { type } = action.payload;
      const newId = `${type}-${Date.now()}`;
      
      // Find next available position
      const maxRow = Math.max(...state.layout.map(m => m.position.row), -1);
      const modulesInLastRow = state.layout.filter(m => m.position.row === maxRow);
      
      let newPosition;
      if (modulesInLastRow.length < 3) {
        newPosition = { row: maxRow, col: modulesInLastRow.length };
      } else {
        newPosition = { row: maxRow + 1, col: 0 };
      }
      
      state.layout.push({
        id: newId,
        type,
        position: newPosition,
        size: { w: 1, h: 1 },
      });
      
      // Save to localStorage
      saveLayout(state.layout);
    },
    removeModule: (state, action) => {
      const { id } = action.payload;
      state.layout = state.layout.filter(m => m.id !== id);
      saveLayout(state.layout);
    },
    updateModulePosition: (state, action) => {
      const { id, position } = action.payload;
      const module = state.layout.find(m => m.id === id);
      if (module) {
        module.position = position;
      }
      saveLayout(state.layout);
    },
    reorderModules: (state, action) => {
      state.layout = action.payload;
      saveLayout(state.layout);
    },
    resetLayout: (state) => {
      state.layout = defaultLayout;
      saveLayout(defaultLayout);
    },
    setNewModuleDropdownOpen: (state, action) => {
      state.newModuleDropdownOpen = action.payload;
    },
  },
});

export const {
  ensureDefaultModules,
  setEditMode,
  toggleEditMode,
  addModule,
  removeModule,
  updateModulePosition,
  reorderModules,
  resetLayout,
  setNewModuleDropdownOpen,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;

